<template>
  <div class="app" :class="`screen-${screen}`">
    <canvas ref="canvas" class="board"></canvas>

    <div v-if="screen !== 'lobby' && game" class="hud">
      <div><span>Score</span><b>{{ game.score.toLocaleString() }}</b><small v-if="bonus > 1" class="bonus">Group bonus {{ bonus }}x</small></div>
      <div><span>Lines</span><b>{{ game.lines }}</b></div>
      <div><span>Level</span><b>{{ game.level }}</b></div>
      <div v-if="roomCode" class="hud-room"><span>Room</span><b>{{ roomCode }}</b></div>
    </div>

    <!-- Who's signed in on this computer. Anyone can sign in. -->
    <div v-if="screen === 'lobby' && accountsOk" class="account-bar">
      <template v-if="user">
        <span class="who">{{ user.displayName || user.email }}</span>
        <button type="button" class="link" @click="showPlayers = true">Players</button>
        <button type="button" class="link" @click="showRecords = true">Records</button>
        <button type="button" class="link" @click="doSignOut">Sign out</button>
      </template>
      <template v-else>
        <button type="button" class="secondary" :disabled="signingIn" @click="doSignIn">Sign in</button>
        <span class="why">to save names, colours and scores</span>
      </template>
    </div>
    <PlayersPanel
      v-if="showPlayers && user"
      :uid="user.uid"
      :people="people"
      @changed="peopleChanged"
      @close="showPlayers = false"
    />
    <RecordsPanel v-if="showRecords && user" :uid="user.uid" @close="showRecords = false" />

    <!-- Lobby: press anything to join (or, in party mode, scan in) -->
    <section v-if="screen === 'lobby'" class="panel lobby">
      <h1>Couch Blocks</h1>
      <p class="tagline">One well. Everyone at once. Shove responsibly.</p>
      <div class="seats">
        <div
          v-for="(seat, i) in seatSlots"
          :key="i"
          class="seat"
          :class="{ filled: seats[i], mine: seats[i] && isMine(seats[i]) }"
          :style="seats[i] ? { '--c': seatColour(i).fill } : null"
        >
          <template v-if="seats[i]">
            <b class="seat-name">
              <span v-if="canFlip(seats[i])" class="flip" aria-hidden="true">&#9664;</span>
              {{ seats[i].name }}
              <span v-if="canFlip(seats[i])" class="flip" aria-hidden="true">&#9654;</span>
            </b>
            <span>{{ seats[i].label }}</span>
            <span v-if="seats[i].remote && mode === 'host' && !seats[i].phone" class="where">online</span>
            <span v-if="mode === 'guest'" class="where">{{ isMine(seats[i]) ? 'you' : 'host' }}</span>
          </template>
          <template v-else>
            <span class="empty">Press any button<br>to join</span>
          </template>
        </div>
      </div>
      <p v-if="party" class="start-hint">
        Phones join by scanning the code below. Start the game here once everyone's in.
      </p>
      <p v-else-if="seats.length" class="start-hint">
        Left / right picks your name. B (or Q) leaves your seat.
        <span v-if="padSeated" class="pad-hint">Up / down on the d-pad picks a button, A presses it, Options starts the game.</span>
      </p>
      <p v-else class="start-hint">Up to forty players. The keyboard is one player (arrows or WASD). Controllers: up to four per computer - more can join online.</p>
      <ul v-if="controlRows.length" class="controls">
        <li v-for="row in controlRows" :key="row.hint">
          <b v-for="i in row.seats" :key="i" :style="{ color: seatColour(i).fill }">{{ seats[i].name }}</b>
          {{ row.hint }}
        </li>
      </ul>

      <button
        v-if="!party && mode !== 'guest'"
        class="primary start-button"
        type="button"
        :disabled="!seats.length"
        @click="hostStart"
      >
        Start
      </button>
      <p v-else-if="mode === 'guest'" class="net-note">The host starts the game.</p>

      <!-- Online: one computer hosts, the others join with the code. -->
      <div v-if="onlineOk" class="online">
        <template v-if="mode === 'local'">
          <button class="secondary" type="button" :disabled="netBusy" @click="hostParty">Party mode (phones)</button>
          <button class="secondary" type="button" :disabled="netBusy" @click="host">Host an online game</button>
          <form class="join" @submit.prevent="join">
            <input
              v-model="joinCode"
              maxlength="4"
              placeholder="CODE"
              autocapitalize="characters"
              autocomplete="off"
              spellcheck="false"
              aria-label="Room code"
            >
            <button class="secondary" type="submit" :disabled="netBusy || joinCode.length < 4">Join</button>
          </form>
        </template>
        <template v-else-if="mode === 'host' && party">
          <div class="party-join">
            <img v-if="partyQr" :src="partyQr" alt="QR code to join the party" width="220" height="220">
            <div>
              <p class="party-title">Party mode</p>
              <p>Scan with your phone's camera, or go to</p>
              <p class="party-url">{{ partyUrlShort }}</p>
              <p>and type <b class="code">{{ roomCode }}</b></p>
              <p class="net-note">Your phone is your controller and your screen - it follows your piece. Up to {{ partyMax }} players.</p>
            </div>
          </div>
          <button class="primary" type="button" :disabled="!seats.length" @click="hostStart">Start the game</button>
          <button class="link" type="button" @click="leaveOnline">End party mode</button>
        </template>
        <template v-else-if="mode === 'host'">
          <p class="code-line">Room code <b class="code">{{ roomCode }}</b> &middot; friends type this under “Join”</p>
          <!-- Or send them a link that joins the room by itself (Matt, 2026-10-30). -->
          <p class="invite"><span class="invite-url">{{ inviteUrl }}</span> <button class="secondary" type="button" @click="shareInvite">{{ inviteCopied ? 'Copied' : 'Copy invite link' }}</button></p>
          <p class="net-note">{{ peerCount ? `${peerCount} computer${peerCount > 1 ? 's' : ''} connected` : 'Waiting for someone to join…' }}</p>
          <button class="link" type="button" @click="leaveOnline">Stop hosting</button>
        </template>
        <template v-else>
          <p class="code-line">In room <b class="code">{{ roomCode }}</b> &middot; the host starts the game</p>
          <button class="link" type="button" @click="leaveOnline">Leave</button>
        </template>
        <p v-if="netMessage" class="net-error">{{ netMessage }}</p>
      </div>

      <p class="rules">
        Your piece is your colour, and each one drops in somewhere new - watch for the flash.
        Slide into someone and you shove them along with you. Falling straight down never pushes.
        A line built by more of you scores more, and three or more of you get a group bonus.
      </p>
      <p class="sound-line">
        <button type="button" class="link" :aria-pressed="soundOn" @click="toggleSfx">Sound {{ soundOn ? 'on' : 'off' }}</button>
        <button type="button" class="link" :aria-pressed="musicOn" @click="toggleMusic">Music {{ musicOn ? 'on' : 'off' }}</button>
        <span class="sound-key">M mutes</span>
      </p>
      <p v-if="!soundReady && (soundOn || musicOn)" class="sound-hint">Click anywhere or press a key to turn the sound on - browsers don't count a controller.</p>
      <p v-if="best && mode !== 'guest'" class="best">Best with {{ seats.length || 2 }}: {{ best.toLocaleString() }}</p>
      <button class="stamp" type="button" title="Tap to reload" @click="reload">{{ stamp }}</button>
    </section>

    <section v-if="screen === 'paused'" class="panel overlay">
      <h2>Paused</h2>
      <p v-if="pauseReason" class="net-error">{{ pauseReason }}</p>
      <p>Options / Start (or Esc) to carry on</p>
      <p class="quit-line">Hold Circle / B (or Q, or /) to leave the game</p>
      <div class="quit-bar" :class="{ active: quitHold > 0 }"><i :style="{ width: `${Math.round(quitHold * 100)}%` }"></i></div>
      <p class="sound-line">
        <button type="button" class="link" :aria-pressed="soundOn" @click="toggleSfx">Sound {{ soundOn ? 'on' : 'off' }}</button>
        <button type="button" class="link" :aria-pressed="musicOn" @click="toggleMusic">Music {{ musicOn ? 'on' : 'off' }}</button>
        <span class="sound-key">M mutes</span>
      </p>
    </section>

    <section v-if="screen === 'over' && game" class="panel overlay">
      <h2>Topped out</h2>
      <p class="final">{{ game.score.toLocaleString() }}</p>
      <p v-if="bonus > 1" class="bonus">Group bonus {{ bonus }}x</p>
      <p>{{ game.lines }} lines · level {{ game.level }}<span v-if="newBest"> · new best!</span></p>
      <p class="per-player-title">Blocks each of you had in cleared lines</p>
      <p class="per-player">
        <span v-for="(p, i) in game.players" :key="i" :style="{ color: seatColour(i).fill }">
          {{ seats[i] ? seats[i].name : seatColour(i).name }} {{ p.cleared }}
        </span>
      </p>
      <div v-if="mode !== 'guest'" class="over-actions">
        <button class="primary" type="button" @click="hostStart">Play again</button>
        <button class="secondary" type="button" @click="hostToLobby">Back to lobby</button>
      </div>
      <p v-else>Waiting for the host to start the next one</p>
    </section>

    <p v-if="mode === 'guest' && hostGone" class="toast-line">The host's game closed.</p>

    <!--
      Full screen, for the TV. Browsers only allow it from a click or a key
      press (a controller button doesn't count), so: this button, or F.
    -->
    <button
      v-if="screen !== 'playing' && fullscreenOk"
      type="button"
      class="fullscreen-toggle"
      :title="isFullscreen ? 'Exit full screen (F)' : 'Full screen (F)'"
      :aria-label="isFullscreen ? 'Exit full screen' : 'Full screen'"
      @click="toggleFullscreen"
    >
      <svg v-if="!isFullscreen" width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5M.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5" />
      </svg>
      <svg v-else width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M5.5 0a.5.5 0 0 1 .5.5v4A1.5 1.5 0 0 1 4.5 6h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5m5 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 10 4.5v-4a.5.5 0 0 1 .5-.5M0 10.5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 6 11.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5m10 1a1.5 1.5 0 0 1 1.5-1.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0z" />
      </svg>
      <span>{{ isFullscreen ? 'Exit full screen' : 'Full screen' }}</span>
    </button>

    <ControlsCard
      v-if="(screen === 'playing' || screen === 'paused') && controlPlayers.length"
      :players="controlPlayers"
      :fits="controlsFit"
    />

    <BugReportButton v-if="screen !== 'playing'" :game-state="bugState" />
    <BugResolutionNotice />
  </div>
</template>

<script>
import {
  createGame, applyInput, step, takeEvents, groupBonus, SCORING_VERSION,
} from './engine/game.js';
import { createDevices, deviceLabel, deviceHint } from './input/devices.js';
import { createRepeater, readActions } from './input/repeat.js';
import { moveFocus, pressFocus, closeTop, clearFocus } from './input/menuNav.js';
import {
  draw, createFx, noteEvents, layout, PLAYER_COLOURS,
} from './render/draw.js';
import { colour, assignColours } from './account/palette.js';
import {
  accountsAvailable, watchAccount, signIn, signOut, loadPeople, recordGame, claimGame,
} from './account/account.js';
import PlayersPanel from './components/PlayersPanel.vue';
import RecordsPanel from './components/RecordsPanel.vue';
import ControlsCard from './components/ControlsCard.vue';
import { packGame, unpackGame } from './net/snapshot.js';
import {
  hostRoom, joinRoom, onlineAvailable, cleanCode,
} from './net/room.js';
import { handOverSeats } from './net/rejoin.js';
import { buildStamp, forceRefresh } from './utils/buildStamp.js';
import { packWindow, followCamera } from './net/snapshot.js';
import BugReportButton from './components/BugReportButton.vue';
import BugResolutionNotice from './components/BugResolutionNotice.vue';
import { createSound } from './audio/sound.js';

const BEST_KEY = 'couchBlocks:best2';
// Bests from before the group bonus, scaled up to match on first read.
const OLD_BEST_KEY = 'couchBlocks:best';
const MAX_STEP_MS = 50;
const LOCAL = 'local';
const MENU_GRACE_MS = 700;
// Matt, 2026-10-01: "I think this game could get more and more fun with
// more people ... Let's go to twelve." One computer can seat five (Chrome
// shows a page at most four gamepads, plus the keyboard); the rest
// join online. Then (2026-10-30), with everyone on their own computer:
// "let's just let the well get larger and larger, it'll be ok" - the same
// cap as Party mode. The whole well still shows on every screen (following
// your own piece, as phones do, is for later).
const MAX_SEATS = 40;
// Party mode (phones as controller + screen) has no couch to fill; this is
// just a sanity cap. Matt, 2026-10-01: "you could sort of have infinite
// players playing all on the same board."
const PARTY_MAX = 40;
// Leaving a paused game means holding turn-back this long. Matt: "from the
// pause screen, it's too easy to reset the game ... maybe a press and hold."
const QUIT_HOLD_MS = 1000;
// What the host passes on to online guests' screens (see broadcastFrame).
const SENT_EVENTS = new Set(['clear', 'push', 'spawn', 'lock', 'drop', 'level', 'over']);

function readBest() {
  try {
    const best = JSON.parse(window.localStorage.getItem(BEST_KEY));
    if (best) return best;
    const old = JSON.parse(window.localStorage.getItem(OLD_BEST_KEY)) || {};
    return Object.fromEntries(Object.entries(old).map(([n, score]) => [n, Math.round(score * groupBonus(Number(n)))]));
  } catch { return {}; }
}
function writeBest(best) {
  try { window.localStorage.setItem(BEST_KEY, JSON.stringify(best)); } catch { /* fine */ }
}

// A seat is one person: which computer they're on (`peer`: 'local' or the
// guest connection's id) and which controls on it (`device`). Online play
// is just more seats - the host runs the one real game and treats a guest's
// controller exactly like one of its own.
const seatKey = (peer, device) => `${peer}|${device}`;
const clampCols = (n) => Math.max(10, Math.min(16, Math.round(Number(n) || 10)));

export default {
  name: 'App',
  components: {
    BugReportButton, BugResolutionNotice, PlayersPanel, RecordsPanel, ControlsCard,
  },
  data() {
    return {
      screen: 'lobby',
      // [{ key, peer, device, label, hint, remote, who, colourKey, name }]
      // `who` is the couch player sitting there ({ uid, pid, name, colour })
      // or null for a guest; colourKey/name are what everyone sees.
      seats: [],
      game: null, // host/local: the engine state; guest: the latest unpacked snapshot
      // accounts
      accountsOk: accountsAvailable(),
      user: null, // a real (Google) sign-in, never the anonymous one
      people: [], // this computer's couch players [{ pid, name, colour }]
      signingIn: false,
      showPlayers: false,
      showRecords: false,
      stamp: buildStamp(),
      bestByCount: readBest(),
      newBest: false,
      tick: 0,
      // online
      onlineOk: onlineAvailable(),
      mode: 'local', // 'local' | 'host' | 'guest'
      roomCode: '',
      joinCode: '',
      netBusy: false,
      netMessage: '',
      peerCount: 0,
      myPeerId: LOCAL, // a guest's id as the host knows it
      pauseReason: '',
      hostGone: false,
      quitHold: 0, // 0..1 while someone holds turn-back on the pause screen
      isFullscreen: false,
      party: false,
      partyQr: '',
      partyUrlShort: '',
      inviteCopied: false,
      partyMax: PARTY_MAX,
      fullscreenOk: Boolean(document.fullscreenEnabled || document.webkitFullscreenEnabled),
      soundOn: true,
      musicOn: true,
      soundReady: false,
    };
  },
  computed: {
    // A controller is sitting in this computer's lobby.
    padSeated() { return this.seats.some((s) => s.peer === LOCAL && String(s.device).startsWith('pad-')); },
    // The whole link to this room: open it and you are in (mounted reads ?join=).
    inviteUrl() { return this.roomCode ? `${window.location.origin}/?join=${this.roomCode}` : ''; },
    bonus() { return groupBonus(this.game ? this.game.players.length : 1); },
    best() { return this.bestByCount[this.seats.length || 2] || 0; },
    // Filled seats plus one open card to join - always four cards at least,
    // so the couch-sized lobby still looks like it always did.
    maxSeats() { return this.party ? PARTY_MAX : MAX_SEATS; },
    seatSlots() {
      return Math.min(this.maxSeats, Math.max(4, this.seats.length + 1));
    },
    // Who is playing at THIS computer, and with what - for the controls
    // card in the corner. On the party TV, phones count as here too: the
    // TV is where a confused phone player looks up.
    controlPlayers() {
      return this.seats
        .map((seat, i) => ({ seat, i }))
        .filter(({ seat }) => this.isMine(seat) || (this.party && seat.phone))
        .map(({ seat, i }) => ({
          kind: seat.phone ? 'phone' : (seat.device.startsWith('pad-') ? 'pad' : 'keys'),
          name: seat.name,
          fill: this.seatColour(i).fill,
        }));
    },
    // Is there room beside the well for the open controls card (~250px)?
    controlsFit() {
      if (!this.game) return true;
      const L = layout(this.game.width, window.innerWidth, window.innerHeight);
      return (window.innerWidth - L.wellW) / 2 >= 280;
    },
    controlRows() {
      const rows = [];
      this.seats.forEach((seat, i) => {
        if (!this.isMine(seat)) return;
        const row = rows.find((r) => r.hint === seat.hint);
        if (row) row.seats.push(i); else rows.push({ hint: seat.hint, seats: [i] });
      });
      return rows;
    },
    bugState() {
      return {
        screen: this.screen,
        mode: this.mode,
        room: this.roomCode || null,
        players: this.seats.map((s) => s.key),
        game: this.game ? { ...this.game, events: [] } : null,
      };
    },
  },
  watch: {
    joinCode(v) {
      const clean = cleanCode(v);
      if (clean !== v) this.joinCode = clean;
    },
  },
  mounted() {
    // An invite link (?join=CODE) joins that room straight away.
    const invited = new URLSearchParams(window.location.search).get('join');
    if (invited && this.onlineOk) {
      window.history.replaceState(null, '', window.location.pathname); // a reload starts fresh
      this.joinCode = invited.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
      if (this.joinCode.length === 4) this.$nextTick(() => this.join());
    }
    this.devices = createDevices();
    this.sound = createSound({ onChange: (s) => this.soundChanged(s) });
    this.soundChanged(this.sound.state());
    // Browsers only let sound start from a click or a key press.
    this.onUnlock = () => this.sound.unlock();
    window.addEventListener('pointerdown', this.onUnlock, true);
    window.addEventListener('keydown', this.onUnlock, true);
    this.prevHeld = {};
    this.fx = createFx();
    this.inbox = []; // host: messages from guests, handled in the frame loop
    this.repeaters = {};
    this.last = performance.now();
    this.onResize = () => this.sizeCanvas();
    window.addEventListener('resize', this.onResize);
    // Keys "stick" when the window loses focus mid-press; a pad can vanish.
    // Either way, stop the game rather than let a piece drift.
    this.onBlur = () => this.pause();
    this.onPadGone = () => this.pause();
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('gamepaddisconnected', this.onPadGone);
    this.sizeCanvas();
    this.preview = createGame({ players: 2, seed: 1 });
    this.frame = requestAnimationFrame(this.loop);
    window.__couchBlocks = this; // for the browser checks and bug triage
    this.stopAccount = watchAccount((user) => this.accountChanged(user));
    this.onFullscreenChange = () => {
      this.isFullscreen = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
    };
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    this.onKey = (e) => {
      if ((e.code !== 'KeyF' && e.code !== 'KeyM') || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target?.closest?.('input, textarea')) return;
      if (e.code === 'KeyM') this.sound.toggleMute();
      else this.toggleFullscreen();
    };
    window.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    cancelAnimationFrame(this.frame);
    this.devices.destroy();
    this.leaveOnline();
    this.stopAccount?.();
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('pointerdown', this.onUnlock, true);
    window.removeEventListener('keydown', this.onUnlock, true);
    this.sound.destroy();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('gamepaddisconnected', this.onPadGone);
  },
  methods: {
    reload() { forceRefresh(); },
    // Blurred after, so Space or Enter (drop) can't flip it again.
    toggleSfx() { this.sound.setSfx(!this.soundOn); document.activeElement?.blur?.(); },
    toggleMusic() { this.sound.setMusic(!this.musicOn); document.activeElement?.blur?.(); },
    soundChanged(s) {
      this.soundOn = s.sfx;
      this.musicOn = s.music;
      this.soundReady = s.ready;
    },
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          await (document.exitFullscreen || document.webkitExitFullscreen).call(document);
        } else {
          const el = document.documentElement;
          await (el.requestFullscreen || el.webkitRequestFullscreen).call(el);
        }
      } catch {
        // Refused (no click behind it, or the browser won't) - nothing to do.
      }
      // Don't leave the button focused: Space would "click" it mid-game.
      document.activeElement?.blur?.();
    },
    isMine(seat) { return seat.peer === this.myPeerId; },
    // Online, mark your own lanes - with four colours from two cities it's
    // the first thing a remote player looks for.
    laneLabels() {
      return this.seats.map((seat, i) => {
        const name = seat.name || this.seatColour(i).name;
        return this.mode !== 'local' && this.isMine(seat) ? `${name} · you` : name;
      });
    },
    seatColour(i) {
      const key = this.seats[i]?.colourKey;
      return key ? colour(key) : PLAYER_COLOURS[i % PLAYER_COLOURS.length];
    },

    // ---- who sits where ---------------------------------------------------

    // The couch players a computer can seat: its account's people, then
    // "guest" (null). Host side only - a guest's computer sends its list.
    optionsFor(peer) {
      const src = peer === LOCAL
        ? { uid: this.user?.uid, people: this.user ? this.people : [] }
        : (this.guestInfo?.[peer] || {});
      const people = (src.uid && src.people) || [];
      return [...people.map((p) => ({
        uid: src.uid, pid: p.pid, name: p.name, colour: p.colour,
      })), null];
    },
    sameWho(a, b) { return Boolean(a && b && a.uid === b.uid && a.pid === b.pid); },
    // Options this seat could switch to: its computer's people nobody else
    // is sitting as, plus guest.
    freeOptionsFor(seat) {
      return this.optionsFor(seat.peer).filter((o) => o === null || this.sameWho(o, seat.who)
        || !this.seats.some((s) => s !== seat && this.sameWho(s.who, o)));
    },
    canFlip(seat) {
      return (this.mode === 'local' || this.isMine(seat) || this.mode === 'host') && this.freeOptionsFor(seat).length > 1;
    },
    flip(seat, dir) {
      const options = this.freeOptionsFor(seat);
      if (options.length < 2) return;
      const at = Math.max(0, options.findIndex((o) => (o === null ? seat.who === null : this.sameWho(o, seat.who))));
      seat.who = options[(at + dir + options.length) % options.length];
      this.roomChanged();
    },
    // After a sign-in, an edit or a guest's list arriving: keep each seat's
    // player current (renamed, recoloured, or gone -> guest).
    refreshWho() {
      this.seats.forEach((seat) => {
        if (!seat.who || seat.phone) return;
        seat.who = this.optionsFor(seat.peer).find((o) => this.sameWho(o, seat.who)) || null;
      });
      this.roomChanged();
    },
    // Colours and names everyone sees. Earlier seats get first claim on a
    // favourite colour; a guest is called by their colour.
    resolveSeats() {
      const keys = assignColours(this.seats.map((s) => s.who?.colour || null));
      this.seats.forEach((seat, i) => {
        seat.colourKey = keys[i];
        seat.name = seat.who?.name || colour(keys[i]).name;
      });
    },

    // ---- accounts -------------------------------------------------------------

    async doSignIn() {
      this.signingIn = true;
      try { await signIn(); } catch { /* closed the popup - nothing to say */ }
      this.signingIn = false;
    },
    async doSignOut() {
      await signOut();
    },
    async accountChanged(user) {
      this.user = user;
      this.people = [];
      if (user) {
        try { this.people = await loadPeople(user); } catch { this.people = []; }
      }
      this.peopleChanged(this.people);
    },
    peopleChanged(people) {
      this.people = people;
      if (this.mode === 'guest') this.sendDevices();
      else this.refreshWho();
    },
    sizeCanvas() {
      const canvas = this.$refs.canvas;
      const dpr = window.devicePixelRatio || 1;
      this.cssW = window.innerWidth;
      this.cssH = window.innerHeight;
      canvas.width = Math.round(this.cssW * dpr);
      canvas.height = Math.round(this.cssH * dpr);
      canvas.style.width = `${this.cssW}px`;
      canvas.style.height = `${this.cssH}px`;
      // desynchronized: draw straight to the screen instead of waiting on
      // the page's compositor - the browser's low-latency mode for games and
      // drawing apps, worth up to a frame between button and block.
      this.ctx = canvas.getContext('2d', { desynchronized: true, alpha: false });
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    },
    // Buttons that went down this frame, per local device.
    presses(held) {
      const out = {};
      for (const [id, buttons] of Object.entries(held)) {
        const prev = this.prevHeld[id] || new Set();
        const down = [...buttons].filter((b) => !prev.has(b));
        if (down.length) out[id] = new Set(down);
      }
      return out;
    },
    // Bug form, notice or the room-code box: the keyboard belongs to it.
    menusBusy() {
      return Boolean(document.querySelector('.bug-backdrop, .bug-resolution-backdrop, .players-backdrop, .records-backdrop'))
        || document.activeElement?.tagName === 'INPUT';
    },

    // ---- the frame ------------------------------------------------------

    loop(now) {
      const dt = Math.min(now - this.last, MAX_STEP_MS);
      this.last = now;
      const held = this.devices.read();
      const busy = this.menusBusy();
      const fresh = this.presses(held);
      const pressed = busy ? {} : fresh;
      this.prevHeld = held;
      if (this.screen !== 'playing') this.menuFrame(fresh, busy);

      if (this.mode === 'guest') this.guestFrame(busy ? {} : held, pressed, dt);
      else this.hostFrame(busy ? {} : held, pressed, dt, now);

      const shown = this.game || this.preview;
      draw(this.ctx, shown, this.fx, {
        cssW: this.cssW,
        cssH: this.cssH,
        now,
        labels: this.game ? this.laneLabels() : [],
        colours: this.seats.map((_, i) => this.seatColour(i)),
        tags: this.party,
        showNext: !this.party,
      });
      this.sound.update(this.screen, this.game?.level);
      this.tick += 1;
      this.frame = requestAnimationFrame(this.loop);
    },

    // Local play and hosting are the same loop: hosting just has more seats
    // and a mailbox.
    hostFrame(held, pressed, dt, now) {
      // Everyone's fresh presses, keyed by seat key.
      const presses = {};
      for (const [device, set] of Object.entries(pressed)) presses[seatKey(LOCAL, device)] = set;
      // Everyone's game actions, keyed by seat key.
      const actions = {};
      this.remoteHeld ||= {};
      for (const msg of this.inbox.splice(0)) {
        const key = seatKey(msg.from, msg.d);
        if (msg.t === 'held') this.remoteHeld[key] = new Set(msg.b);
        if (msg.t === 'press') presses[key] = new Set([...(presses[key] || []), ...msg.b]);
        if (msg.t === 'act') actions[key] = [...(actions[key] || []), ...msg.a];
      }

      if (this.screen === 'lobby') this.lobbyFrame(presses);
      else if (this.screen === 'playing') this.playFrame(held, actions, dt, now);
      else if (this.screen === 'paused') {
        const heldBy = (seat) => (seat.peer === LOCAL ? held[seat.device] : this.remoteHeld[seat.key]) || new Set();
        this.pausedFrame(presses, heldBy, dt);
      }

      if (this.mode === 'host') this.broadcastFrame();
    },
    lobbyFrame(presses) {
      for (const [key, buttons] of Object.entries(presses)) {
        const seat = this.seats.findIndex((s) => s.key === key);
        if (seat === -1) {
          // Phones sit down by sending their name, not by pressing.
          if (!key.endsWith('|touch') && this.seats.length < this.maxSeats) this.addSeat(key);
        } else if (buttons.has('left') || buttons.has('right')) {
          this.flip(this.seats[seat], buttons.has('left') ? -1 : 1);
        } else if (buttons.has('back')) {
          this.seats.splice(seat, 1);
          this.roomChanged();
        }
      }
    },
    addSeat(key) {
      const [peer, device] = key.split('|');
      const remote = peer !== LOCAL;
      const info = remote ? (this.guestInfo?.[peer]?.devices?.[device] || {}) : {};
      const seat = {
        key,
        peer,
        device,
        remote,
        label: remote ? (info.label || device) : deviceLabel(device),
        hint: remote ? (info.hint || '') : deviceHint(device),
        who: null,
      };
      // Sit down as the first of this computer's players nobody is yet.
      seat.who = this.optionsFor(peer).find((o) => o && !this.seats.some((s) => this.sameWho(s.who, o))) || null;
      this.seats.push(seat);
      this.roomChanged();
    },
    start() {
      this.game = createGame({ players: this.seats.length, spawn: this.party ? 'near' : 'anywhere' });
      this.cams = this.seats.map(() => 0);
      this.repeaters = {};
      this.fx = createFx();
      this.sound.restartMusic();
      this.newBest = false;
      this.pauseReason = '';
      this.setScreen('playing');
    },
    playFrame(held, remoteActions, dt, now) {
      this.seats.forEach((seat, i) => {
        let actions;
        if (seat.peer === LOCAL) {
          this.repeaters[seat.key] ||= createRepeater(held[seat.device]);
          actions = readActions(this.repeaters[seat.key], held[seat.device] || new Set(), dt);
        } else {
          actions = remoteActions[seat.key] || [];
        }
        actions.forEach((a) => {
          if (a === 'start') this.pause();
          else applyInput(this.game, i, a);
        });
      });
      if (this.screen !== 'playing') return;
      step(this.game, dt);
      const events = takeEvents(this.game);
      noteEvents(this.fx, events, now);
      this.sound.play(events);
      if (events.length) this.pendingEvents = [...(this.pendingEvents || []), ...events];
      if (this.game.over) this.finish();
    },
    pause() {
      if (this.mode === 'guest' || this.screen !== 'playing') return;
      this.game.paused = true;
      this.setScreen('paused');
    },
    pausedFrame(presses, heldBy, dt) {
      const holding = this.seats.some((seat) => heldBy(seat).has('back'));
      this.quitMs = holding ? (this.quitMs || 0) + dt : 0;
      const shown = Math.min(1, this.quitMs / QUIT_HOLD_MS);
      if (shown !== this.quitHold) {
        this.quitHold = shown;
        // Guests see the bar fill too.
        if (this.mode === 'host') this.broadcastFrame(true);
      }
      if (this.quitMs >= QUIT_HOLD_MS) {
        this.quitMs = 0;
        this.quitHold = 0;
        this.toLobby();
        return;
      }
      for (const seat of this.seats) {
        const b = presses[seat.key];
        if (!b) continue;
        if (b.has('start')) {
          this.game.paused = false;
          this.pauseReason = '';
          // Fresh repeaters - seeded with what's held right now (see
          // createRepeater), so the Start that resumed doesn't pause again.
          this.repeaters = {};
          this.quitMs = 0;
          this.quitHold = 0;
          this.setScreen('playing');
          return;
        }
      }
    },
    finish() {
      this.setScreen('over');
      this.saveGame();
      const n = this.seats.length;
      if (this.game.score > (this.bestByCount[n] || 0)) {
        this.bestByCount = { ...this.bestByCount, [n]: this.game.score };
        writeBest(this.bestByCount);
        this.newBest = true;
      }
    },
    // Every finished game is kept, signed in or not; the accounts that were
    // in it (here and on guests' computers) each add it to their list.
    async saveGame() {
      const g = this.game;
      const record = {
        online: this.mode === 'host',
        score: g.score,
        scoring: SCORING_VERSION,
        lines: g.lines,
        level: g.level,
        ms: Math.round(g.time),
        players: this.seats.map((seat, i) => ({
          uid: seat.who?.uid || null,
          pid: seat.who?.pid || null,
          name: seat.name,
          colour: seat.colourKey,
          lines: g.players[i].lines,
          cleared: g.players[i].cleared,
          pieces: g.players[i].pieces,
        })),
      };
      try {
        const id = await recordGame(record);
        if (id && this.room) this.room.peers.forEach((p) => p.send({ t: 'recorded', id }));
      } catch {
        // A game that couldn't be saved is a lost row in the records,
        // nothing worse - never interrupt the game-over screen for it.
      }
    },
    // Starting (and starting again) is the host's click, never a game
    // button. Matt: "using the drop button as the start is annoying ...
    // Maybe the host just has to click on start to start." Someone still
    // hammering drop as the stack tops out no longer restarts the game.
    hostStart() {
      if (!this.seats.length || this.mode === 'guest') return;
      document.activeElement?.blur?.();
      this.start();
    },
    hostToLobby() {
      document.activeElement?.blur?.();
      this.toLobby();
    },
    // A party where everyone is on a phone has nobody at the computer to
    // click Play again (a player's report: "there needs to be a way to
    // restart the game and/or get back to the lobby from my phone"). So a
    // seated phone can do the over screen's two buttons, and leave a paused
    // game - phones can't do the hold-to-quit controllers use.
    phoneMenu(peer, action) {
      if (!this.seats.some((s) => s.key === seatKey(peer.id, 'touch'))) return;
      if (action === 'again' && this.screen === 'over') this.hostStart();
      if (action === 'lobby' && (this.screen === 'over' || this.screen === 'paused')) {
        this.quitMs = 0;
        this.quitHold = 0;
        this.hostToLobby();
      }
    },
    toLobby() {
      this.game = null;
      this.fx = createFx();
      this.setScreen('lobby');
    },
    setScreen(screen) {
      this.screen = screen;
      this.screenAt = performance.now();
      clearFocus();
      this.roomChanged();
    },
    // A controller works the menus too (Matt on his Steam Deck, 2026-11-03:
    // "no way for me to access the sign in button ... or the start button").
    // Up / down on the d-pad walks a highlight, A presses it, B closes a
    // panel, Options is Start. Only a SEATED pad in the lobby: an unseated
    // one's first press is how it sits down. And not for a moment after the
    // screen changes, so the turn someone was hammering as the stack topped
    // out doesn't press Play again (same guard as Blockout's).
    menuFrame(pressed, panelOpen) {
      if (performance.now() - (this.screenAt || 0) < MENU_GRACE_MS) return;
      for (const [device, b] of Object.entries(pressed)) {
        if (!device.startsWith('pad-')) continue;
        if (!this.soundReady) this.sound.unlock();
        if (panelOpen) {
          if (b.has('drop')) moveFocus('up');
          else if (b.has('down')) moveFocus('down');
          else if (b.has('ccw')) pressFocus();
          else if (b.has('back')) closeTop();
          continue;
        }
        if (this.screen === 'lobby' && !this.seats.some((s) => s.peer === LOCAL && s.device === device)) continue;
        if (b.has('drop')) moveFocus('up');
        else if (b.has('down')) moveFocus('down');
        else if (b.has('ccw')) pressFocus();
        else if (b.has('start') && this.screen !== 'paused') this.hostStart();
      }
    },

    // ---- hosting ----------------------------------------------------------

    // Party mode: host a room, and put a QR code for phones on the TV.
    async hostParty() {
      this.party = true;
      await this.host();
      if (this.mode !== 'host') { this.party = false; return; }
      const url = `${window.location.origin}/?party=${this.roomCode}`;
      this.partyUrlShort = window.location.host;
      try {
        const QRCode = (await import('qrcode')).default;
        this.partyQr = await QRCode.toDataURL(url, {
          width: 440, margin: 1, color: { dark: '#12141c', light: '#eef0f6' },
        });
      } catch {
        this.partyQr = '';
      }
    },
    phoneJoined(peer, msg) {
      const key = seatKey(peer.id, 'touch');
      const name = String(msg.name || 'Player').trim().slice(0, 16) || 'Player';
      const existing = this.seats.find((s) => s.key === key);
      if (existing) {
        existing.who = { uid: null, pid: null, name, colour: msg.colour };
      } else {
        if (this.screen !== 'lobby' || this.seats.length >= this.maxSeats) {
          peer.send({ t: 'full' });
          return;
        }
        this.seats.push({
          key,
          peer: peer.id,
          device: 'touch',
          remote: true,
          phone: true,
          label: 'Phone',
          hint: '',
          who: { uid: null, pid: null, name, colour: msg.colour },
          cols: clampCols(msg.cols),
        });
      }
      this.roomChanged();
    },
    async host() {
      this.netBusy = true;
      this.netMessage = '';
      this.guestInfo = {};
      this.clientOf = {};
      try {
        this.room = await hostRoom({
          onPeerOpen: (peer) => {
            this.rejoined(peer);
            this.peerCount = this.room?.peers.size || 1;
            peer.send({ t: 'hello', you: peer.id });
            this.sendRoom(peer);
          },
          onPeerClosed: (peer) => this.peerLeft(peer),
          onMessage: (peer, msg) => {
            if (msg.t === 'phone') {
              this.phoneJoined(peer, msg);
              return;
            }
            if (msg.t === 'phone-cols') {
              const seat = this.seats.find((x) => x.key === seatKey(peer.id, 'touch'));
              if (seat) seat.cols = clampCols(msg.cols);
              return;
            }
            if (msg.t === 'phone-menu') {
              this.phoneMenu(peer, msg.a);
              return;
            }
            if (msg.t === 'devices') {
              this.guestInfo[peer.id] = { devices: msg.devices, uid: msg.uid || null, people: msg.people || [] };
              this.refreshWho();
              return;
            }
            this.inbox.push({ ...msg, from: peer.id });
          },
        });
        this.roomCode = this.room.code;
        this.mode = 'host';
      } catch (error) {
        this.netMessage = error.message || 'Could not start an online game.';
      }
      this.netBusy = false;
    },
    // A tab we've seen before is back on a new connection (see rejoin.js):
    // its seats, held buttons and labels move over, and the old connection,
    // if it hasn't noticed it's dead yet, is dropped without a fuss. A game
    // paused for their drop stays paused until someone presses Start.
    rejoined(peer) {
      this.clientOf ||= {};
      if (!peer.client) return;
      const oldPeers = Object.keys(this.clientOf).filter((id) => id !== peer.id && this.clientOf[id] === peer.client);
      this.clientOf[peer.id] = peer.client;
      if (!oldPeers.length) return;
      oldPeers.forEach((id) => {
        delete this.clientOf[id];
        const stale = this.room?.peers.get(id);
        if (stale) {
          this.room.peers.delete(id);
          stale.close();
        }
      });
      const moved = handOverSeats(this.seats, oldPeers, peer.id, seatKey);
      Object.entries(moved).forEach(([was, now]) => {
        if (this.remoteHeld?.[was]) this.remoteHeld[now] = this.remoteHeld[was];
        delete this.remoteHeld?.[was];
      });
      const info = oldPeers.map((id) => this.guestInfo?.[id]).find(Boolean);
      if (info && !this.guestInfo[peer.id]) this.guestInfo[peer.id] = info;
      oldPeers.forEach((id) => { delete this.guestInfo?.[id]; });
      if (Object.keys(moved).length) {
        if (this.screen === 'paused') this.pauseReason = '';
        this.roomChanged();
      }
    },
    peerLeft(peer) {
      this.peerCount = this.room?.peers.size || 0;
      const theirs = this.seats.filter((s) => s.peer === peer.id);
      if (!theirs.length) return;
      if (this.screen === 'lobby') {
        this.seats = this.seats.filter((s) => s.peer !== peer.id);
        this.roomChanged();
      } else {
        // Mid-game the well can't shrink; stop so everyone notices. Their
        // pieces just fall if you carry on.
        const names = theirs.map((s) => s.name).join(' and ');
        this.pause();
        this.pauseReason = `${names} lost their connection.`;
        this.roomChanged();
      }
    },
    // The lobby/screen picture every guest needs, sent whenever it changes.
    roomState() {
      return {
        t: 'room',
        party: this.party,
        screen: this.screen,
        pauseReason: this.pauseReason,
        newBest: this.newBest,
        seats: this.seats.map(({
          key, peer, device, label, hint, who, colourKey, name,
        }) => ({
          key, peer, device, label, hint, who, colourKey, name,
        })),
      };
    },
    sendRoom(peer) { peer.send(this.roomState()); },
    roomChanged() {
      if (this.mode === 'guest') return;
      this.resolveSeats();
      if (this.mode !== 'host' || !this.room) return;
      const msg = this.roomState();
      this.room.peers.forEach((p) => p.send(msg));
      // A fresh picture right away, so a screen change never waits a frame.
      if (this.game) this.broadcastFrame(true);
    },
    broadcastFrame(force = false) {
      if (!this.room || !this.room.peers.size || !this.game) return;
      if (!force && this.screen !== 'playing' && !this.pendingEvents?.length) {
        // Nothing moves while paused or over; a few pictures a second is plenty.
        if (this.tick % 15 !== 0) return;
      }
      const msg = {
        t: 'snap', screen: this.screen, g: packGame(this.game), q: this.quitHold,
      };
      const events = this.pendingEvents || [];
      this.pendingEvents = [];
      const phoneSeat = {};
      this.seats.forEach((seat, i) => { if (seat.phone) phoneSeat[seat.peer] = i; });
      this.room.peers.forEach((p) => {
        if (phoneSeat[p.id] !== undefined) {
          // A phone gets only its window - a few hundred bytes - and only
          // every other frame: thirty pictures a second is plenty for a
          // phone, and it keeps a forty-phone party off the host's uplink.
          const i = phoneSeat[p.id];
          if (force || this.tick % 2 === 0) {
            const cols = this.seats[i].cols;
            this.cams[i] = followCamera(this.cams[i] ?? 0, this.game, i, cols);
            p.sendSnap({ t: 'win', screen: this.screen, win: packWindow(this.game, i, this.cams[i], cols) });
          }
        } else {
          p.sendSnap(msg);
        }
        // Line clears, shoves and arrivals go on the reliable channel: the
        // flash, the bump and the "here you are" are how you know. Locks,
        // drops, level-ups and the end ride along for the sound. (Moves and
        // turns don't: a guest hears its own as it presses.)
        if (events.length) p.send({ t: 'events', events: events.filter((e) => SENT_EVENTS.has(e.type)) });
      });
    },

    // ---- joining -------------------------------------------------------------

    /** Share the invite link: the share sheet where there is one (phones), the clipboard otherwise. */
    async shareInvite() {
      const url = this.inviteUrl;
      try {
        if (navigator.share && matchMedia('(pointer: coarse)').matches) { await navigator.share({ title: 'Join my game', url }); return; }
        await navigator.clipboard.writeText(url);
        this.inviteCopied = true;
        setTimeout(() => { this.inviteCopied = false; }, 2000);
      } catch { /* cancelled, or no clipboard: the link is on screen to copy by hand */ }
    },

    async join() {
      this.netBusy = true;
      this.netMessage = '';
      try {
        const { code, peer } = await joinRoom(this.joinCode, {
          onMessage: (_, msg) => this.fromHost(msg),
          onClosed: () => this.hostLeft(),
        });
        this.hostPeer = peer;
        this.roomCode = code;
        this.mode = 'guest';
        this.seats = [];
        this.game = null;
        this.hostGone = false;
        this.sendDevices();
      } catch (error) {
        this.netMessage = error.message || 'Could not join that game.';
      }
      this.netBusy = false;
    },
    // Labels for this computer's controls, so the host's lobby can name them.
    sendDevices() {
      const devices = {};
      Object.keys(this.devices.read()).forEach((id) => {
        devices[id] = { label: deviceLabel(id), hint: deviceHint(id) };
      });
      this.knownDevices = Object.keys(devices).join(',');
      // And who plays at this computer, so its seats can pick names too.
      this.hostPeer?.send({
        t: 'devices', devices, uid: this.user?.uid || null, people: this.user ? this.people : [],
      });
    },
    fromHost(msg) {
      if (msg.t === 'hello') this.myPeerId = msg.you;
      if (msg.t === 'room') {
        this.seats = msg.seats.map((s) => ({ ...s, remote: s.peer !== LOCAL }));
        this.pauseReason = msg.pauseReason || '';
        this.newBest = msg.newBest;
        if (msg.screen !== this.screen) {
          if (msg.screen === 'playing' && this.screen !== 'paused') {
            this.fx = createFx();
            this.sound.restartMusic();
          }
          this.repeaters = {};
          this.screen = msg.screen;
          if (msg.screen === 'lobby') this.game = null;
        }
      }
      if (msg.t === 'snap') {
        if (msg.screen === 'lobby') return;
        this.game = unpackGame(msg.g);
        this.quitHold = msg.q || 0;
      }
      if (msg.t === 'events') {
        noteEvents(this.fx, msg.events, performance.now());
        this.sound.play(msg.events);
      }
      if (msg.t === 'recorded') claimGame(msg.id).catch(() => {});
    },
    guestFrame(held, pressed, dt) {
      if (!this.hostPeer) return;
      // A controller switched on after joining needs a name in the lobby.
      if (Object.keys(held).join(',') !== this.knownDevices) this.sendDevices();
      if (this.screen === 'playing') {
        // Hold-to-repeat runs HERE, on the guest's own clock, so a held
        // direction slides at the same speed it would on the couch; only the
        // resulting moves cross the network.
        for (const [device, buttons] of Object.entries(held)) {
          const key = seatKey(this.myPeerId, device);
          if (!this.seats.some((s) => s.key === key)) continue;
          this.repeaters[device] ||= createRepeater(buttons);
          const actions = readActions(this.repeaters[device], buttons, dt);
          if (actions.length) {
            this.hostPeer.send({ t: 'act', d: device, a: actions });
            this.sound.playActions(actions, this.seats.findIndex((s) => s.key === key));
          }
        }
      } else {
        for (const [device, buttons] of Object.entries(pressed)) {
          this.hostPeer.send({ t: 'press', d: device, b: [...buttons] });
        }
        // What's held, whenever it changes - the pause screen's
        // hold-to-leave needs to know a button is still down.
        this.sentHeld ||= {};
        for (const [device, buttons] of Object.entries(held)) {
          const now = [...buttons].sort().join(',');
          if (now === (this.sentHeld[device] || '')) continue;
          this.sentHeld[device] = now;
          this.hostPeer.send({ t: 'held', d: device, b: [...buttons] });
        }
      }
    },
    hostLeft() {
      if (this.mode !== 'guest') return;
      this.hostGone = true;
      this.leaveOnline();
      setTimeout(() => { this.hostGone = false; }, 5000);
    },

    leaveOnline() {
      if (this.mode === 'host') {
        this.room?.close();
        this.room = null;
        this.seats = this.seats.filter((s) => s.peer === LOCAL);
        if (this.screen !== 'lobby') this.toLobby();
      }
      if (this.mode === 'guest') {
        this.hostPeer?.close();
        this.hostPeer = null;
        this.seats = [];
        this.game = null;
        this.screen = 'lobby';
      }
      this.mode = 'local';
      this.party = false;
      this.partyQr = '';
      this.seats = this.seats.filter((s) => !s.phone);
      this.roomCode = '';
      this.peerCount = 0;
      this.myPeerId = LOCAL;
    },
  },
};
</script>

<style>
:root { color-scheme: dark; --safe-bottom: env(safe-area-inset-bottom, 0px); }
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; height: 100%; background: #12141c; color: #eef0f6; overflow: hidden; }
body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; }
button { font: inherit; cursor: pointer; }
button:disabled { cursor: default; }
button.primary { background: #ff6b5b; color: #12141c; border: 0; border-radius: 8px; padding: 0.5rem 1rem; font-weight: 700; }
button.secondary { background: transparent; color: #eef0f6; border: 1px solid rgba(255,255,255,0.3); border-radius: 8px; padding: 0.5rem 1rem; }
.update-reload-toast { position: fixed; top: 1rem; left: 50%; transform: translateX(-50%); background: #1c1c1c; color: #fff; padding: 0.5rem 1rem; border-radius: 8px; z-index: 2000; }
</style>

<style scoped>
.app { position: fixed; inset: 0; }
.board { position: absolute; inset: 0; display: block; }
.screen-lobby .board { opacity: 0.25; filter: blur(2px); }

.hud {
  position: absolute; top: 1rem; left: 1rem;
  display: flex; flex-direction: column; gap: 0.6rem;
  font-variant-numeric: tabular-nums;
}
.hud div { display: flex; flex-direction: column; }
.hud span { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.6; }
.hud b { font-size: 1.6rem; }
/* Bigger on a big screen, but still inside the 120px gutter layout() keeps
   clear down the left for it (draw.js SIDE_GUTTER). */
@media (min-width: 1200px) and (min-height: 800px) {
  .hud span { font-size: 0.85rem; }
  .hud b { font-size: 1.95rem; }
}

.panel {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  width: min(920px, calc(100vw - 32px));
  max-height: calc(100vh - 32px); overflow-y: auto;
  text-align: center;
}
.overlay {
  background: rgba(18, 20, 28, 0.88); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px; padding: 1.5rem;
}
h1 { font-size: clamp(2.2rem, 6vw, 3.6rem); margin: 0; letter-spacing: -0.02em; }
h2 { margin: 0 0 0.5rem; font-size: 2rem; }
.tagline { opacity: 0.7; margin: 0.3rem 0 1.5rem; }
.seats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; }
@media (max-width: 560px) { .seats { grid-template-columns: repeat(2, 1fr); } }
.seat {
  min-height: 84px; border-radius: 12px; border: 2px dashed rgba(255,255,255,0.18);
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.3rem;
  padding: 0.5rem; font-size: 0.85rem;
}
.seat.filled { border: 2px solid var(--c); background: color-mix(in srgb, var(--c) 18%, transparent); }
.seat.filled b { color: var(--c); font-size: 1.1rem; }
.seat .empty { opacity: 0.5; }
.start-hint { margin: 1.25rem 0 0.5rem; }
.pad-hint { display: block; margin-top: 0.25rem; }
/* The controller's place in a menu. */
.pad-focus { outline: 3px solid #fff; outline-offset: 3px; border-radius: 6px; }
.controls { list-style: none; padding: 0; margin: 0 0 1rem; font-size: 0.85rem; opacity: 0.85; }
.controls b { margin-right: 0.4rem; }
.rules { font-size: 0.85rem; opacity: 0.6; max-width: 34rem; margin: 0.5rem auto; }
.best { font-size: 0.85rem; opacity: 0.7; }
.sound-line { display: flex; gap: 1rem; justify-content: center; align-items: baseline; font-size: 0.85rem; margin: 0.5rem 0; }
.sound-key { opacity: 0.5; }
.sound-hint { font-size: 0.8rem; opacity: 0.6; margin: 0.25rem 0; }
.stamp {
  background: none; border: 0; color: #eef0f6; opacity: 0.4; font-size: 0.75rem; margin-top: 1rem;
}
.final { font-size: 3rem; font-weight: 800; margin: 0; }
.bonus { color: #ffbf3c; font-weight: 700; }
.hud small.bonus { font-size: 0.75rem; }
.panel .bonus { margin: 0.2rem 0 0; }
.per-player { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
</style>

<style scoped>
.seat .where { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.6; }
.seat.mine { box-shadow: 0 0 0 3px color-mix(in srgb, var(--c) 35%, transparent); }
.online {
  margin: 1.25rem auto 0.5rem; display: flex; flex-wrap: wrap; gap: 0.75rem;
  align-items: center; justify-content: center;
}
.online p { margin: 0; flex-basis: 100%; }
.join { display: flex; gap: 0.5rem; }
.join input {
  width: 6.5rem; font: inherit; font-weight: 700; letter-spacing: 0.2em; text-align: center;
  text-transform: uppercase; background: #1a1d29; color: #eef0f6;
  border: 1px solid rgba(255,255,255,0.3); border-radius: 8px; padding: 0.45rem 0.5rem;
}
.code-line { font-size: 1rem; }
.invite { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 0.6rem; margin: 0.4rem 0; }
.invite-url { font-family: ui-monospace, Menlo, monospace; font-size: 0.95rem; padding: 0.35rem 0.6rem; border-radius: 6px; background: rgba(255,255,255,0.08); user-select: all; }
.code { font-size: 1.6rem; letter-spacing: 0.2em; color: #ffbf3c; margin: 0 0.2rem; }
.net-note { font-size: 0.85rem; opacity: 0.7; }
.net-error { color: #ff8f84; font-size: 0.9rem; }
button.link { background: none; border: 0; color: #eef0f6; opacity: 0.6; text-decoration: underline; }
button.secondary:disabled { opacity: 0.4; }
.hud-room b { color: #ffbf3c; letter-spacing: 0.1em; }
.toast-line {
  position: fixed; top: 1rem; left: 50%; transform: translateX(-50%); margin: 0;
  background: #1c1c1c; padding: 0.5rem 1rem; border-radius: 8px;
}
</style>

<style scoped>
.account-bar {
  position: absolute; top: 1rem; right: 1rem; z-index: 5;
  display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; justify-content: flex-end;
  max-width: calc(100vw - 32px); font-size: 0.9rem;
}
.account-bar .who { font-weight: 700; }
.account-bar .why { opacity: 0.6; font-size: 0.8rem; }
@media (max-width: 560px) { .account-bar { position: static; justify-content: center; margin: 1rem 16px 0; } }
.seat-name { display: flex; align-items: center; gap: 0.35rem; }
.flip { font-size: 0.6rem; opacity: 0.55; }
</style>

<style scoped>
.quit-line { opacity: 0.7; font-size: 0.9rem; margin-bottom: 0.4rem; }
.quit-bar {
  height: 6px; border-radius: 3px; background: rgba(255, 255, 255, 0.12); overflow: hidden;
  width: min(260px, 80%); margin: 0 auto; opacity: 0.5;
}
.quit-bar.active { opacity: 1; }
.quit-bar i { display: block; height: 100%; background: #ff6b5b; }
</style>

<style scoped>
.fullscreen-toggle {
  position: fixed; right: 0.75rem; bottom: calc(0.75rem + var(--safe-bottom)); z-index: 700;
  display: flex; align-items: center; gap: 0.45rem;
  background: rgba(30, 33, 46, 0.85); color: #c4c8d8; border: 1px solid #3a3f55;
  border-radius: 999px; padding: 0.45rem 0.85rem; font-size: 0.85rem;
}
.fullscreen-toggle:hover { color: #fff; }
</style>

<style scoped>
.party-join {
  display: flex; gap: 1.25rem; align-items: center; justify-content: center; flex-wrap: wrap;
  flex-basis: 100%; text-align: left;
}
.party-join img { border-radius: 12px; background: #eef0f6; width: 220px; height: 220px; }
.party-join p { margin: 0.2rem 0; }
.party-title { font-size: 1.4rem; font-weight: 800; }
.party-url { font-size: 1.2rem; font-weight: 700; color: #4cc9f0; }
</style>

<style scoped>
.start-button { font-size: 1.25rem; padding: 0.7rem 2.5rem; margin: 0.25rem auto 0.5rem; display: block; }
.start-button:disabled { opacity: 0.35; }
.per-player-title { margin: 0.75rem 0 0.2rem; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.6; }
.over-actions { display: flex; gap: 0.75rem; justify-content: center; margin-top: 1rem; }
</style>
